// ============================================================
// Page « Paramètres » : règles de saisie des paramètres connus.
// Les noms viennent de ParametreType (API), qui reste la référence et
// revérifie tout à l'enregistrement. Un paramètre inconnu de cette table
// garde le libellé de l'API et une saisie libre.
// ============================================================

// Même format que le VO Email de l'API (Email.estValide).
const FORMAT_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Valeur posée à la création d'un paramètre (commande parametre:sync) : pour
// une liste d'adresses, elle signifie « rien de saisi ».
const VALEUR_PAR_DEFAUT = "0";

export const CHAMPS_PARAMETRES = {
  DELAY_BEFORE_FIRST_RDV: { type: "nombre", min: 0, unite: "jours" },
  NB_DOSSIER_MAX_PAR_EP: { type: "nombre", min: 1, unite: "dossiers" },
  DESTINATAIRES_RECAP_SUSPENSION_REMOBILISATION: {
    type: "emails",
    label: "Destinataires du récapitulatif des Suspensions / Remobilisations",
    aide:
      "Adresses e-mail séparées par des virgules. Le récapitulatif des demandes en attente de décision " +
      "part chaque jour à 10h.",
    siVide: "Aucun destinataire : le récapitulatif ne part pas tant qu'aucune adresse n'est saisie.",
  },
};

export const champParametre = (valeur) => {
  const connu = CHAMPS_PARAMETRES[valeur.name] || {};
  return { type: "texte", ...connu, name: valeur.name, label: connu.label || valeur.label };
};

export const valeurAffichee = (valeur) =>
  champParametre(valeur).type === "emails" && valeur.value === VALEUR_PAR_DEFAUT ? "" : valeur.value ?? "";

export const saisiesInitiales = (groupes) =>
  Object.fromEntries(groupes.flatMap((groupe) => groupe.values.map((valeur) => [valeur.name, valeurAffichee(valeur)])));

export const adressesSaisies = (texte) =>
  String(texte ?? "")
    .split(/[,;]/)
    .map((adresse) => adresse.trim())
    .filter(Boolean);

// Message d'erreur d'une saisie, ou null si elle est valide.
export const validerSaisie = (champ, saisie) => {
  const texte = String(saisie ?? "").trim();
  if (champ.type === "nombre") {
    return /^\d+$/.test(texte) && Number(texte) >= champ.min
      ? null
      : `Saisissez un nombre entier supérieur ou égal à ${champ.min}.`;
  }
  if (champ.type === "emails") {
    const adresses = adressesSaisies(texte);
    if (adresses.length === 0) {
      return "Saisissez au moins une adresse e-mail, par exemple prenom.nom@cafreunion.fr.";
    }
    const invalides = adresses.filter((adresse) => !FORMAT_EMAIL.test(adresse));
    return invalides.length
      ? `Adresse e-mail invalide : ${invalides.join(", ")}. Séparez les adresses par des virgules.`
      : null;
  }
  return texte ? null : "Saisissez une valeur.";
};

export const estModifie = (saisies, reference) =>
  Object.keys(reference).some((name) => saisies[name] !== reference[name]);

// Seules les valeurs modifiées sont vérifiées, comme côté API : une valeur
// restée à son défaut ne bloque pas l'enregistrement des autres paramètres.
export const validerSaisies = (groupes, saisies, reference) => {
  const erreurs = {};
  groupes.forEach((groupe) =>
    groupe.values.forEach((valeur) => {
      if (saisies[valeur.name] === reference[valeur.name]) return;
      const message = validerSaisie(champParametre(valeur), saisies[valeur.name]);
      if (message) erreurs[valeur.name] = message;
    })
  );
  return erreurs;
};

// Corps du PATCH constants/dynamic : les groupes tels que reçus, avec les
// valeurs saisies ; une valeur non modifiée repart telle quelle (le « 0 »
// par défaut d'une liste d'adresses compris).
export const parametresAEnvoyer = (groupes, saisies, reference) =>
  groupes.map((groupe) => ({
    ...groupe,
    values: groupe.values.map((valeur) => ({
      ...valeur,
      value: saisies[valeur.name] === reference[valeur.name] ? valeur.value : String(saisies[valeur.name]).trim(),
    })),
  }));
